import { LatLngLiteral } from "leaflet";
import { CaseReducer, PayloadAction, createSlice } from "@reduxjs/toolkit";
import { isArray, uniqueId } from "lodash";

import { Executor } from "../../../services";
import { DEFAUT_VALUEE_FILTERS } from "../../../components/common/Modal/ArchiveOrderFiltersModal/constants";
import { Tab } from "../../../pages/MainPage/pages/Orders/components/OrderModal/types/Tab";
import { ORDERS_TABLE_PAGE_SIZE, OrderType } from "../../constants/OrdersPage";
import { sameOrderItems } from "../../../utils/ordersHotPath";

import {
	OrderStatusContainer,
	OrderStatusContainers,
	Companies,
	Modal,
	ModalState,
	Filter,
	OrderPageState,
	FilterBtns,
	ByExecutor,
	ByCar,
	ByOrder,
	ByMulty,
	byMultyFilters,
} from "./interface";

export type { OrderStatusContainer, OrderStatusContainers, Companies };

const DEFAULT_PAGINATION = {
	limit: undefined,
	count: 0,
	offset: 0,
};

const ORDERS_TEMPLATE: OrderStatusContainers = {
	live: {
		items: [],
		count: 0,
		pagination: DEFAULT_PAGINATION,
	},
	closed: {
		items: [],
		count: 0,
		pagination: DEFAULT_PAGINATION,
	},
	preliminary: {
		items: [],
		count: 0,
		pagination: DEFAULT_PAGINATION,
	},
	all: { items: [], count: 0, pagination: DEFAULT_PAGINATION },
	executable: {
		items: [],
		count: 0,
		pagination: DEFAULT_PAGINATION,
	},
};

const defaultOrder = {
	points: [],
	distance: 0,
	route: [],
	hash: uniqueId("order"),
};

const pathTranslationsMain = "redux.reducers.ordersPage.modals.";

const standartTabFilter = {
	limit: ORDERS_TABLE_PAGE_SIZE,
	byCar: undefined,
	byExecutor: undefined,
	byMulty: undefined,
	byMultyFilters: DEFAUT_VALUEE_FILTERS,
};

const initialState: OrderPageState = {
	modal: {
		open: false,
		data: {
			cards: [],
			activeCardId: null,
			dispatcherId: null,
		},
	},
	companies: { items: [], pagination: {} },
	hideOrderIds: [],
	orders: ORDERS_TEMPLATE,
	tabs: [], // * data for tabs in modal edit/add order
	bufferOpenNewTab: false, // * data for tabs in modal edit/add order
	bufferFirstOpenThenCallUp: false, // * data for first cal open  -  modal edit/add order
	bufferOpenBaseThenCallContinue: false, // * open button or key then continues call - modal edit/add order
	activePoint: -1,
	activeOrder: {
		points: [],
		distance: 0,
		route: [],
	},
	searchTypes: ["street"],
	ordersType: "live",
	mapCenter: { lat: 50.4501, lng: 30.5234 },
	modals: [
		{
			label: `${pathTranslationsMain}chat`,
			type: "chat",
			position: "",
			groupId: "",
			visible: true,
			columnStart: 0,
			rowStart: 0,
			columnEnd: 0,
			rowEnd: 0,
		},
		{
			label: `${pathTranslationsMain}table`,
			type: "table",
			position: "",
			groupId: "",
			visible: true,
			columnStart: 0,
			rowStart: 0,
			columnEnd: 0,
			rowEnd: 0,
		},
		{
			label: `${pathTranslationsMain}map`,
			type: "map",
			position: "",
			groupId: "",
			visible: true,
			columnStart: 0,
			rowStart: 0,
			columnEnd: 0,
			rowEnd: 0,
		},
		{
			label: `${pathTranslationsMain}parking`,
			type: "parking",
			position: "",
			groupId: "",
			visible: true,
			columnStart: 0,
			rowStart: 0,
			columnEnd: 0,
			rowEnd: 0,
		},
		{
			label: `${pathTranslationsMain}executors`,
			type: "executors",
			position: "",
			groupId: "",
			visible: true,
			columnStart: 0,
			rowStart: 0,
			columnEnd: 0,
			rowEnd: 0,
		},
		{
			label: `${pathTranslationsMain}softphone`,
			type: "softphone",
			position: "",
			groupId: "",
			visible: false,
			columnStart: 0,
			rowStart: 0,
			columnEnd: 0,
			rowEnd: 0,
		},
		{
			label: `${pathTranslationsMain}previewer`,
			type: "previewer",
			position: "",
			groupId: "",
			visible: true,
			columnStart: 0,
			rowStart: 0,
			columnEnd: 0,
			rowEnd: 0,
		},
	],
	executorsByCarCallsign: [],
	smsTemplates: [],
	filters: {
		live: standartTabFilter,
		closed: {
			dateRange: {
				from: new Date(Date.now() - 24 * 60 * 60 * 1000),
				to: new Date(Date.now() - 24 * 60 * 60 * 1000),
			},
			...standartTabFilter,
		},
		preliminary: standartTabFilter,
		all: standartTabFilter,
		executable: standartTabFilter,
	},
	filtersBtns: { showOnlyMyOrders: false, showOrdersWithoutExecutors: false },
};

type Reducer<P> = CaseReducer<OrderPageState, PayloadAction<P>>;

const setModalOpen: Reducer<boolean> = (state, { payload }) => {
	state.modal.open = payload;

	if (payload === false) {
		state.tabs = initialState.tabs;
	} //* Clearing the data cache in the edit modal order
};

const setBufferOpenNewTab: Reducer<boolean> = (state, { payload }) => {
	state.bufferOpenNewTab = payload;
};

const setBufferFirstOpenThenCallUp: Reducer<boolean> = (state, { payload }) => {
	state.bufferFirstOpenThenCallUp = payload;
};

const setBufferOpenBaseThenCallContinue: Reducer<boolean> = (
	state,
	{ payload },
) => {
	state.bufferOpenBaseThenCallContinue = payload;
};

const setModalData: Reducer<ModalState["data"]> = (state, { payload }) => {
	state.modal.data = payload;
};

const updatedModalData: Reducer<any> = (state, { payload }) => {
	if (!payload) return;
	try {
		const { cards } = state.modal.data;
		const newCards = cards?.map((card) => {
			if (card.id === payload?.id) {
				return { ...card, order: { ...card.order, ...payload } };
			}
			return card;
		});
		state.modal.data.cards = newCards;
	} catch (error) {
		console.error(error);
	}
};

const modals: Reducer<Modal[]> = (state, { payload }) => {
	state.modals = payload;
};

const setOrdersType: Reducer<OrderType> = (state, { payload }) => {
	if (state.ordersType !== payload) {
		const prev = state.ordersType;
		// Pagination lives only on CLOSED — reset its limit when leaving the tab.
		if (prev === "closed") {
			const prevFilter = state.filters[prev];
			if (
				prevFilter?.limit &&
				prevFilter.limit !== ORDERS_TABLE_PAGE_SIZE
			) {
				state.filters[prev] = {
					...prevFilter,
					limit: ORDERS_TABLE_PAGE_SIZE,
				};
			}
		}
	}
	state.ordersType = payload;
	state.activeOrder = state.orders[payload]?.items?.at(0) ?? defaultOrder;
	state.activePoint = 0;
};

const setActiveOrderBeDefault: Reducer<undefined> = (state) => {
	state.activeOrder = defaultOrder;
	state.activePoint = 0;
};

const setTabs: Reducer<Tab[]> = (state, { payload }) => {
	state.tabs = payload;
};

const syncActiveOrder = (state: OrderPageState) => {
	const items = state.orders[state.ordersType]?.items;
	const activeOrder =
		items?.find((item) => item.id === state.activeOrder?.id) ??
		items?.at(0) ??
		defaultOrder;

	if (activeOrder === state.activeOrder) return;

	if ("id" in activeOrder && activeOrder.id === state.activeOrder?.id) {
		state.activePoint = Math.min(
			activeOrder.points.length - 1,
			state.activePoint,
		);
	} else {
		state.activePoint = 0;
	}

	state.activeOrder = activeOrder;
};

const setOrdersByTab: Reducer<{
	tab: keyof OrderStatusContainers;
	data: OrderStatusContainer;
}> = (state, { payload }) => {
	const prev = state.orders[payload.tab];
	if (
		prev &&
		sameOrderItems(prev.items, payload.data.items) &&
		prev.count === payload.data.count
	) {
		return;
	}

	state.orders[payload.tab] = payload.data;

	if (payload.tab === state.ordersType) {
		syncActiveOrder(state);
	}
};

const setActivePoint: Reducer<number> = (state, { payload }) => {
	state.activePoint = payload;
};

const setMapCenter: Reducer<LatLngLiteral> = (state, { payload }) => {
	state.mapCenter = payload;
};

const setActiveOrder: Reducer<any> = (state, { payload }) => {
	state.activeOrder = payload;
};

const setModalVisible: Reducer<{
	type: any;
	visible: any;
}> = (state, { payload }) => {
	const newArray = state.modals.map((item) =>
		item.type === payload.type
			? { ...item, visible: payload.visible }
			: item,
	);

	state.modals = newArray;
};

const addHideOrderId: Reducer<{ id: number }> = (state, { payload }) => {
	state.hideOrderIds.push(payload.id);
};

const removeHideOrderId: Reducer<{ id: number }> = (state, { payload }) => {
	state.hideOrderIds = state.hideOrderIds.filter((id) => id !== payload.id);
};

const setCompanies: Reducer<Companies> = (state, { payload }) => {
	state.companies = payload;
};

const setExecutorsByCarCallsign: Reducer<Executor.Model[]> = (
	state,
	{ payload },
) => {
	state.executorsByCarCallsign = payload;
};

const setSMSTemplates: Reducer<any[]> = (state, { payload }) => {
	state.smsTemplates = payload;
};

const setFilters: Reducer<{ type: OrderType | OrderType[]; data: Filter }> = (
	state,
	{ payload },
) => {
	if (isArray(payload.type)) {
		payload.type.forEach((type) => {
			state.filters[type] = payload.data;
		});
	} else {
		state.filters[payload.type] = payload.data;
	}
};

const setFiltersByExecutor: Reducer<{
	type: OrderType | OrderType[];
	data: ByExecutor;
}> = (state, { payload }) => {
	if (isArray(payload.type)) {
		payload.type.forEach((type) => {
			state.filters[type].byExecutor = payload.data;
		});
	} else {
		state.filters[payload.type].byExecutor = payload.data;
	}
};

const setFiltersByCar: Reducer<{
	type: OrderType | OrderType[];
	data: ByCar;
}> = (state, { payload }) => {
	if (isArray(payload.type)) {
		payload.type.forEach((type) => {
			state.filters[type].byCar = payload.data;
		});
	} else {
		state.filters[payload.type].byCar = payload.data;
	}
};

const setFiltersByOrder: Reducer<{
	type: OrderType | OrderType[];
	data: ByOrder;
}> = (state, { payload }) => {
	if (isArray(payload.type)) {
		payload.type.forEach((type) => {
			state.filters[type].byOrder = payload.data;
		});
	} else {
		state.filters[payload.type].byOrder = payload.data;
	}
};

const setFiltersByMulty: Reducer<{
	type: OrderType | OrderType[];
	data?: ByMulty;
}> = (state, { payload }) => {
	if (isArray(payload.type)) {
		payload.type.forEach((type) => {
			state.filters[type].byMulty = payload.data;
		});
	} else {
		state.filters[payload.type].byMulty = payload.data;
	}
};

const setFiltersByMultyFilters: Reducer<{
	type: OrderType | OrderType[];
	data: byMultyFilters;
}> = (state, { payload }) => {
	if (isArray(payload.type)) {
		payload.type.forEach((type) => {
			state.filters[type].byMultyFilters = payload.data;
		});
	} else {
		state.filters[payload.type].byMultyFilters = payload.data;
	}
};

const setFiltersBtns: Reducer<{
	data: FilterBtns;
}> = (state, { payload }) => {
	state.filtersBtns = payload.data;
};

const orderPage = createSlice({
	name: "orderPageReducer",
	initialState,
	reducers: {
		setModalOpen,
		setModalData,
		modals,
		setOrdersType,
		setTabs,
		setBufferOpenNewTab,
		setBufferFirstOpenThenCallUp,
		setBufferOpenBaseThenCallContinue,
		setOrdersByTab,
		setActiveOrderBeDefault,
		setActivePoint,
		setMapCenter,
		setFiltersByExecutor,
		setFiltersByCar,
		setFiltersByOrder,
		setFiltersByMulty,
		setFiltersByMultyFilters,
		setActiveOrder,
		setModalVisible,
		setCompanies,
		setExecutorsByCarCallsign,
		setSMSTemplates,
		setFilters,
		setFiltersBtns,
		updatedModalData,
		addHideOrderId,
		removeHideOrderId,
	},
});

export default orderPage;
